import { useState, type CSSProperties, type FC, type FormEvent } from 'react';
import type { ClientRequest } from '../types';

interface ClientRequestsOverlayProps {
  requests: ClientRequest[];
  isOpen: boolean;
  onClose: () => void;
  onOpenCreate: () => void;
  onAddClarification: (requestId: string, message: string) => Promise<void>;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'COMPLETED':
      return 'Завершена';
    case 'IN_PROGRESS':
      return 'В работе';
    case 'IN_REVIEW':
      return 'На проверке';
    case 'SUBMITTED':
      return 'Подана';
    case 'NEEDS_CLARIFICATION':
      return 'На уточнении';
    default:
      return status;
  }
}

function statusAccent(status: string): string {
  switch (status) {
    case 'COMPLETED':
      return '#49ddb7';
    case 'IN_PROGRESS':
    case 'IN_REVIEW':
    case 'SUBMITTED':
      return '#41d2ff';
    case 'NEEDS_CLARIFICATION':
      return '#e2ad3a';
    default:
      return '#8faebb';
  }
}

function urgencyLabel(urgency: string): string {
  if (urgency === 'FAST') return 'Ускоренный';
  if (urgency === 'URGENT') return 'Срочный';
  if (urgency === 'NORMAL') return 'Стандартный';
  return urgency;
}

function serviceLabel(type: string): string {
  switch (type) {
    case 'ARTICLE':
      return 'Статья';
    case 'DISSERTATION':
      return 'Диссертация';
    case 'TRANSLATION':
      return 'Перевод';
    case 'PLAGIARISM_REWRITE':
      return 'Антиплагиат';
    case 'GRANT':
      return 'Грант';
    case 'CONSULTING':
      return 'Консалтинг';
    default:
      return type;
  }
}

/** Client requests — cab-overlay chrome + cabinet-* body. */
export const ClientRequestsOverlay: FC<ClientRequestsOverlayProps> = ({
  requests,
  isOpen,
  onClose,
  onOpenCreate,
  onAddClarification,
}) => {
  const [filter, setFilter] = useState('ALL');
  const [selectedId, setSelectedId] = useState(requests[0]?.id || '');
  const [clarificationText, setClarificationText] = useState('');
  const [isSending, setIsSending] = useState(false);

  if (!isOpen) return null;

  const filtered = requests.filter((r) => {
    if (filter === 'ALL') return true;
    if (filter === 'IN_PROGRESS') return ['IN_PROGRESS', 'IN_REVIEW', 'SUBMITTED'].includes(r.status);
    if (filter === 'NEEDS_CLARIFICATION') return r.status === 'NEEDS_CLARIFICATION';
    if (filter === 'COMPLETED') return r.status === 'COMPLETED';
    return true;
  });

  const selected = requests.find((r) => r.id === selectedId) || filtered[0] || requests[0];

  const handleClarificationSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!clarificationText.trim() || !selected) return;
    try {
      setIsSending(true);
      await onAddClarification(selected.id, clarificationText.trim());
      setClarificationText('');
    } finally {
      setIsSending(false);
    }
  };

  const tabs = [
    { id: 'ALL', label: 'Все заявки', count: requests.length },
    {
      id: 'IN_PROGRESS',
      label: 'В работе',
      count: requests.filter((r) =>
        ['IN_PROGRESS', 'IN_REVIEW', 'SUBMITTED'].includes(r.status),
      ).length,
    },
    {
      id: 'NEEDS_CLARIFICATION',
      label: 'На уточнении',
      count: requests.filter((r) => r.status === 'NEEDS_CLARIFICATION').length,
    },
    {
      id: 'COMPLETED',
      label: 'Завершенные / Готовые',
      count: requests.filter((r) => r.status === 'COMPLETED').length,
    },
  ];

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true" aria-labelledby="requests-title">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Заявки клиента</small>
              <h2 id="requests-title">Мои заявки</h2>
              <p>Реестр обращений, диалог с координаторами и готовые результаты</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn primary" onClick={onOpenCreate}>
              + Создать
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="cabinet-toolbar" style={{ padding: '12px 18px' }}>
          <div className="cabinet-filters">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`cabinet-filter${filter === tab.id ? ' active' : ''}`}
                onClick={() => setFilter(tab.id)}
              >
                {tab.label} · {tab.count}
              </button>
            ))}
          </div>
        </div>

        <div
          className="cab-body"
          style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 0.9fr) minmax(0, 1.2fr)', gap: 14 }}
        >
          <aside className="cabinet-stack">
            {filtered.length === 0 ? (
              <div className="cabinet-note">Нет заявок в данной категории</div>
            ) : (
              filtered.map((r) => {
                const isSel = selected?.id === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    className="cabinet-card"
                    style={
                      {
                        '--accent': isSel ? '#e2ad3a' : statusAccent(r.status),
                        textAlign: 'left',
                        cursor: 'pointer',
                        width: '100%',
                        color: 'inherit',
                        font: 'inherit',
                      } as CSSProperties
                    }
                    onClick={() => setSelectedId(r.id)}
                  >
                    <div className="cabinet-actions" style={{ marginBottom: 6 }}>
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': '#e2ad3a' } as CSSProperties}
                      >
                        {r.request_number}
                      </span>
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': statusAccent(r.status) } as CSSProperties}
                      >
                        {statusLabel(r.status)}
                      </span>
                    </div>
                    <strong style={{ display: 'block' }}>{r.title}</strong>
                    <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12 }}>
                      {r.description || 'Без описания'}
                    </p>
                    <div className="cabinet-actions" style={{ marginTop: 10 }}>
                      <span style={{ fontSize: 11, color: '#8faebb' }}>
                        {r.target_deadline || 'Срок не указан'}
                      </span>
                      <span className="cabinet-spacer" />
                      <span style={{ fontSize: 12, color: '#e2ad3a', fontWeight: 700 }}>
                        {r.budget || '—'}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </aside>

          <main className="cabinet-stack">
            {selected ? (
              <>
                <section className="cabinet-card" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
                  <div className="cabinet-actions">
                    <span
                      className="cabinet-badge"
                      style={{ '--badge': '#e2ad3a' } as CSSProperties}
                    >
                      {selected.request_number} · {serviceLabel(selected.service_type)}
                    </span>
                    <span className="cabinet-spacer" />
                    <span style={{ fontSize: 12, color: '#8faebb' }}>
                      Создана: {new Date(selected.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 style={{ margin: '10px 0 0', fontSize: 18 }}>{selected.title}</h3>
                  <div className="cabinet-actions" style={{ marginTop: 10 }}>
                    <span className="cabinet-badge" style={{ '--badge': '#8faebb' } as CSSProperties}>
                      Отрасль: {selected.field_of_science || '—'}
                    </span>
                    <span className="cabinet-badge" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
                      Срочность: {urgencyLabel(selected.urgency)}
                    </span>
                    <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                      Менеджер: {selected.assigned_manager || 'Не назначен'}
                    </span>
                  </div>
                </section>

                {selected.ready_result_path ? (
                  <div className="cabinet-note ok">
                    <div className="cabinet-actions">
                      <strong>Готовый результат доступен для скачивания</strong>
                      <span className="cabinet-spacer" />
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': '#49ddb7' } as CSSProperties}
                      >
                        Результат принят
                      </span>
                    </div>
                    {selected.ready_result_notes ? (
                      <p style={{ margin: '8px 0 0' }}>{selected.ready_result_notes}</p>
                    ) : null}
                    <div className="cabinet-actions" style={{ marginTop: 10 }}>
                      <span style={{ fontSize: 12 }}>
                        {selected.ready_result_path.split('/').pop()}
                      </span>
                      <span className="cabinet-spacer" />
                      <a
                        href={selected.ready_result_path}
                        target="_blank"
                        rel="noreferrer"
                        className="cabinet-btn primary"
                      >
                        Скачать результат
                      </a>
                    </div>
                  </div>
                ) : null}

                <section className="cabinet-card" style={{ '--accent': '#41d2ff' } as CSSProperties}>
                  <h4 style={{ margin: '0 0 10px', fontSize: 13 }}>
                    Уточнения и переписка с координатором
                  </h4>
                  <div className="cabinet-stack">
                    {selected.clarifications && selected.clarifications.length > 0 ? (
                      selected.clarifications.map((c) => (
                        <div
                          key={c.id}
                          className="cabinet-note"
                          style={
                            c.sender_role === 'CLIENT'
                              ? { borderLeftColor: '#e2ad3a' }
                              : { borderLeftColor: '#41d2ff' }
                          }
                        >
                          <div className="cabinet-actions" style={{ marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: '#8faebb' }}>
                              {c.sender_name || c.sender_role}
                            </span>
                            <span className="cabinet-spacer" />
                            <span style={{ fontSize: 11, color: '#8faebb' }}>
                              {new Date(c.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                          <p style={{ margin: 0 }}>{c.message}</p>
                        </div>
                      ))
                    ) : (
                      <div className="cabinet-note">
                        Вопросов по заявке нет. Все данные предоставлены в полном объеме.
                      </div>
                    )}
                  </div>
                  <form
                    onSubmit={handleClarificationSubmit}
                    className="cabinet-compose"
                    style={{ marginTop: 12 }}
                  >
                    <input
                      className="cabinet-input"
                      type="text"
                      value={clarificationText}
                      onChange={(e) => setClarificationText(e.target.value)}
                      placeholder="Написать уточнение или комментарий координатору..."
                    />
                    <button
                      type="submit"
                      className="cabinet-btn primary"
                      disabled={isSending || !clarificationText.trim()}
                    >
                      {isSending ? 'Отправка…' : 'Ответить'}
                    </button>
                  </form>
                </section>

                <section className="cabinet-card" style={{ '--accent': '#8faebb' } as CSSProperties}>
                  <h4 style={{ margin: '0 0 10px', fontSize: 13 }}>История изменений и этапов</h4>
                  <div className="cabinet-stack">
                    {selected.history && selected.history.length > 0 ? (
                      selected.history.map((h) => (
                        <div key={h.id} className="cabinet-note">
                          <div className="cabinet-actions">
                            <span>
                              <strong>{statusLabel(h.status)}:</strong>{' '}
                              {h.change_reason || 'Статус изменен'}
                            </span>
                            <span className="cabinet-spacer" />
                            <span style={{ fontSize: 11, color: '#8faebb' }}>
                              {new Date(h.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="cabinet-note">История не зафиксирована</div>
                    )}
                  </div>
                </section>
              </>
            ) : (
              <div className="cabinet-note">Выберите заявку для просмотра информации</div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
